/**
 * PercentInput — atlas Input with an in-box trailing "%" suffix.
 * Value is a whole-percent canonical string ("15" = 15%); decimals
 * allowed ("9.6"). Mirrors MoneyInput's controlled contract — including
 * the synchronous local buffer so typing paints instantly even when the
 * parent's re-render is deferred (see `useDeferredSetter`).
 */
import { useEffect, useRef, useState } from "react";
import { Input } from "@tricura/atlas-ui";
import { decimalDigits } from "../utils/money";

interface PercentInputProps {
    /** Whole-percent raw value, e.g. "15" or "9.6". */
    value: string;
    onChange?: (raw: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    isDisabled?: boolean;
    isInvalid?: boolean;
    size?: "sm" | "md" | "lg";
    "aria-label"?: string;
    /** Extra class on the outer wrapper (e.g. width caps). */
    className?: string;
}

export function PercentInput({
    value,
    onChange,
    onBlur,
    placeholder = "0",
    isDisabled,
    isInvalid,
    size = "sm",
    "aria-label": ariaLabel,
    className,
}: PercentInputProps) {
    const lastEmitted = useRef(value);
    const [local, setLocal] = useState(value);
    useEffect(() => {
        if (value !== lastEmitted.current) {
            lastEmitted.current = value;
            setLocal(value);
        }
    }, [value]);

    const handleChange = (raw: string) => {
        const next = decimalDigits(raw);
        lastEmitted.current = next;
        setLocal(next);
        onChange?.(next);
    };

    return (
        <div className={`relative w-full ${className ?? ""}`}>
            <Input
                aria-label={ariaLabel}
                size={size}
                placeholder={placeholder}
                value={local}
                onChange={handleChange}
                onBlur={onBlur}
                isDisabled={isDisabled}
                isInvalid={isInvalid}
                inputMode="decimal"
                autoComplete="off"
                inputClassName="pr-8 text-right"
            />
            <span aria-hidden="true" className="text-tertiary pointer-events-none absolute right-3 top-1/2 z-10 -translate-y-1/2 text-sm">
                %
            </span>
        </div>
    );
}
